'use client';
import React, { useState, useEffect, useCallback } from 'react';
import PublicNav from '@/components/PublicNav';
import HomeFooter from '@/app/components/HomeFooter';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useRouter } from 'next/navigation';
import { ShoppingBag, Cpu, Wrench, Plus, X, Search, Filter, MapPin, Tag, ChevronDown, Loader2, Package, AlertCircle, CheckCircle2 } from 'lucide-react';

type ListingCategory = 'physical_goods' | 'digital_products' | 'services';
type ListingStatus = 'active' | 'sold' | 'closed' | 'draft';

interface Listing {
  id: string;
  seller_id: string;
  title: string;
  description: string;
  category: ListingCategory;
  price: number;
  currency: string;
  location: string;
  contact_info: string;
  image_url: string | null;
  image_alt: string | null;
  tags: string[];
  listing_status: ListingStatus;
  views_count: number;
  created_at: string;
  user_profiles: { full_name: string; is_verified: boolean } | null;
}

interface CreateListingForm {
  title: string;
  description: string;
  category: ListingCategory;
  price: string;
  currency: string;
  location: string;
  contact_info: string;
  tags: string;
}

const CATEGORY_CONFIG: Record<ListingCategory, { label: string; icon: React.ReactNode; color: string; bg: string }> = {
  physical_goods: { label: 'Physical Goods', icon: <Package size={15} />, color: 'text-amber-600', bg: 'bg-amber-50 border-amber-200' },
  digital_products: { label: 'Digital Products', icon: <Cpu size={15} />, color: 'text-violet-600', bg: 'bg-violet-50 border-violet-200' },
  services: { label: 'Services', icon: <Wrench size={15} />, color: 'text-cyan-600', bg: 'bg-cyan-50 border-cyan-200' },
};

const CURRENCIES = ['USD', 'EUR', 'GBP', 'ZAR', 'NGN', 'KES', 'GHS'];

function formatPrice(price: number, currency: string) {
  const symbols: Record<string, string> = { USD: '$', EUR: '€', GBP: '£', ZAR: 'R', NGN: '₦', KES: 'KSh', GHS: '₵' };
  return `${symbols[currency] || currency}${price.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const days = Math.floor(diff / 86400000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 30) return `${days}d ago`;
  return `${Math.floor(days / 30)}mo ago`;
}

export default function MarketplacePage() {
  const { user } = useAuth();
  const router = useRouter();
  const supabase = createClient();

  const [listings, setListings] = useState<Listing[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState<ListingCategory | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [verifiedLoading, setVerifiedLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [sortBy, setSortBy] = useState<'newest' | 'price_asc' | 'price_desc'>('newest');

  const [form, setForm] = useState<CreateListingForm>({
    title: '',
    description: '',
    category: 'physical_goods',
    price: '',
    currency: 'USD',
    location: '',
    contact_info: '',
    tags: '',
  });

  const fetchListings = useCallback(async () => {
    setLoading(true);
    let query = supabase
      .from('marketplace_listings')
      .select('id, seller_id, title, description, category, price, currency, location, contact_info, image_url, image_alt, tags, listing_status, views_count, created_at, user_profiles(full_name, is_verified)')
      .eq('listing_status', 'active');

    if (activeCategory !== 'all') {
      query = query.eq('category', activeCategory);
    }

    if (sortBy === 'newest') query = query.order('created_at', { ascending: false });
    else if (sortBy === 'price_asc') query = query.order('price', { ascending: true });
    else query = query.order('price', { ascending: false });

    const { data, error } = await query.limit(50);
    if (!error && data) setListings(data as Listing[]);
    setLoading(false);
  }, [activeCategory, sortBy, supabase]);

  useEffect(() => { fetchListings(); }, [fetchListings]);

  useEffect(() => {
    if (!user) return;
    setVerifiedLoading(true);
    supabase
      .from('user_profiles')
      .select('is_verified')
      .eq('id', user.id)
      .single()
      .then(({ data }) => {
        setIsVerified(data?.is_verified === true);
        setVerifiedLoading(false);
      });
  }, [user, supabase]);

  const filteredListings = listings.filter((l) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      l.title.toLowerCase().includes(q) ||
      l.description.toLowerCase().includes(q) ||
      l.tags.some((t) => t.toLowerCase().includes(q)) ||
      l.location.toLowerCase().includes(q)
    );
  });

  async function handleCreateListing(e: React.FormEvent) {
    e.preventDefault();
    if (!user) { router.push('/sign-up-login-screen?mode=login'); return; }
    setSubmitting(true);
    setSubmitError('');

    const tagsArray = form.tags
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);

    const { error } = await supabase.from('marketplace_listings').insert({
      seller_id: user.id,
      title: form.title.trim(),
      description: form.description.trim(),
      category: form.category,
      price: parseFloat(form.price) || 0,
      currency: form.currency,
      location: form.location.trim(),
      contact_info: form.contact_info.trim(),
      tags: tagsArray,
      listing_status: 'active',
    });

    if (error) {
      if (error.code === '42501') {
        setSubmitError('Only verified users can create listings. Please complete identity verification in your profile.');
      } else {
        setSubmitError(error.message || 'Failed to create listing. Please try again.');
      }
      setSubmitting(false);
      return;
    }

    setSubmitSuccess(true);
    setSubmitting(false);
    setTimeout(() => {
      setShowCreateModal(false);
      setSubmitSuccess(false);
      setForm({ title: '', description: '', category: 'physical_goods', price: '', currency: 'USD', location: '', contact_info: '', tags: '' });
      fetchListings();
    }, 1500);
  }

  function handleOpenCreate() {
    if (!user) { router.push('/sign-up-login-screen?mode=login'); return; }
    setShowCreateModal(true);
  }

  return (
    <div className="min-h-screen bg-background">
      <PublicNav />

      {/* Hero */}
      <section className="bg-card border-b border-border">
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10 2xl:px-16 py-12 lg:py-16">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <span className="text-xs font-semibold text-primary uppercase tracking-widest">Monezite Marketplace</span>
              </div>
              <h1 className="text-3xl lg:text-4xl font-extrabold text-foreground leading-tight">
                Buy & Sell in the<br className="hidden sm:block" /> Monezite Community
              </h1>
              <p className="mt-3 text-muted-foreground max-w-xl">
                Physical goods, digital products, and services — all from verified community members. Payments arranged directly between buyer and seller.
              </p>
              <div className="flex flex-wrap items-center gap-3 mt-5">
                {Object.entries(CATEGORY_CONFIG).map(([key, cfg]) => (
                  <div key={key} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-semibold ${cfg.bg} ${cfg.color}`}>
                    {cfg.icon}
                    {cfg.label}
                  </div>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <button
                onClick={handleOpenCreate}
                className="flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground text-sm font-semibold rounded-xl hover:bg-cyan-600 transition-all duration-150 active:scale-95"
              >
                <Plus size={16} />
                Post a Listing
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Filters & Search */}
      <section className="sticky top-16 z-30 bg-background/95 backdrop-blur border-b border-border">
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10 2xl:px-16 py-3 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
          {/* Search */}
          <div className="relative flex-1 max-w-sm">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search listings..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-sm bg-muted border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
            />
          </div>

          {/* Category tabs */}
          <div className="flex items-center gap-1 overflow-x-auto">
            {([['all', 'All'], ['physical_goods', 'Physical'], ['digital_products', 'Digital'], ['services', 'Services']] as const).map(([val, label]) => (
              <button
                key={val}
                onClick={() => setActiveCategory(val)}
                className={`px-3 py-1.5 text-xs font-semibold rounded-lg whitespace-nowrap transition-colors ${activeCategory === val ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-muted'}`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Sort */}
          <div className="relative shrink-0">
            <Filter size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
              className="pl-7 pr-7 py-2 text-xs font-medium bg-muted border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 appearance-none cursor-pointer"
            >
              <option value="newest">Newest</option>
              <option value="price_asc">Price: Low to High</option>
              <option value="price_desc">Price: High to Low</option>
            </select>
            <ChevronDown size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          </div>
        </div>
      </section>

      {/* Listings Grid */}
      <main className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10 2xl:px-16 py-10">
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 size={28} className="animate-spin text-primary" />
          </div>
        ) : filteredListings.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 gap-4 text-muted-foreground">
            <ShoppingBag size={40} className="opacity-20" />
            <p className="text-base font-medium">No listings found</p>
            <p className="text-sm">Be the first to post in this category</p>
            <button onClick={handleOpenCreate} className="mt-2 flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground text-sm font-semibold rounded-xl hover:bg-cyan-600 transition-colors">
              <Plus size={15} /> Post a Listing
            </button>
          </div>
        ) : (
          <>
            <p className="text-xs text-muted-foreground mb-5">{filteredListings.length} listing{filteredListings.length !== 1 ? 's' : ''} found</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {filteredListings.map((listing) => {
                const cat = CATEGORY_CONFIG[listing.category];
                const sellerName = listing.user_profiles?.full_name || 'Community Member';
                return (
                  <div key={listing.id} className="bg-card border border-border rounded-2xl overflow-hidden card-shadow hover:card-shadow-md hover:border-primary/30 transition-all duration-200 flex flex-col group">
                    {/* Image or placeholder */}
                    {listing.image_url ? (
                      <div className="h-44 overflow-hidden bg-muted">
                        <img
                          src={listing.image_url}
                          alt={listing.image_alt || listing.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                    ) : (
                      <div className={`h-44 flex items-center justify-center ${cat.bg}`}>
                        <span className={`${cat.color} opacity-40`}>
                          {listing.category === 'physical_goods' ? <Package size={40} /> : listing.category === 'digital_products' ? <Cpu size={40} /> : <Wrench size={40} />}
                        </span>
                      </div>
                    )}

                    <div className="p-4 flex flex-col gap-3 flex-1">
                      {/* Category badge */}
                      <div className="flex items-center justify-between">
                        <span className={`flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border ${cat.bg} ${cat.color}`}>
                          {cat.icon}
                          {cat.label}
                        </span>
                        {listing.user_profiles?.is_verified && (
                          <span className="flex items-center gap-0.5 text-[10px] font-semibold text-success">
                            <CheckCircle2 size={11} />
                            Verified
                          </span>
                        )}
                      </div>

                      {/* Title */}
                      <h3 className="text-sm font-bold text-foreground leading-snug line-clamp-2 group-hover:text-primary transition-colors">
                        {listing.title}
                      </h3>

                      {/* Description */}
                      <p className="text-xs text-muted-foreground line-clamp-2 leading-relaxed flex-1">
                        {listing.description}
                      </p>

                      {/* Tags */}
                      {listing.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {listing.tags.slice(0, 3).map((tag) => (
                            <span key={tag} className="flex items-center gap-0.5 text-[10px] font-medium text-muted-foreground bg-muted px-1.5 py-0.5 rounded-md">
                              <Tag size={9} />
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}

                      {/* Price */}
                      <div className="flex items-center justify-between pt-1 border-t border-border/50">
                        <span className="text-base font-extrabold text-foreground">
                          {formatPrice(listing.price, listing.currency)}
                        </span>
                        <span className="text-[10px] text-muted-foreground">{timeAgo(listing.created_at)}</span>
                      </div>

                      {/* Seller + Location + Contact */}
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <div className="w-5 h-5 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
                            <span className="text-[9px] font-bold text-primary">{sellerName.charAt(0)}</span>
                          </div>
                          <span className="text-xs text-muted-foreground truncate">{sellerName}</span>
                        </div>
                        {listing.location && (
                          <span className="flex items-center gap-0.5 text-[10px] text-muted-foreground shrink-0">
                            <MapPin size={9} />
                            {listing.location}
                          </span>
                        )}
                      </div>

                      {/* Contact CTA */}
                      <div className="mt-1 p-2.5 bg-muted/60 rounded-xl border border-border/50">
                        <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide mb-0.5">Payment & Contact</p>
                        <p className="text-xs text-foreground leading-snug">{listing.contact_info || 'Contact seller via platform messaging'}</p>
                        <p className="text-[10px] text-muted-foreground mt-1 flex items-center gap-1">
                          <AlertCircle size={9} />
                          Payments arranged off-platform between buyer &amp; seller
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </main>

      <HomeFooter />

      {/* Create Listing Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-border">
              <div>
                <h2 className="text-base font-bold text-foreground">Post a Listing</h2>
                <p className="text-xs text-muted-foreground mt-0.5">Only verified users can post. Payments are off-platform.</p>
              </div>
              <button onClick={() => { setShowCreateModal(false); setSubmitError(''); setSubmitSuccess(false); }} className="p-1.5 rounded-lg hover:bg-muted transition-colors">
                <X size={18} className="text-muted-foreground" />
              </button>
            </div>

            {submitSuccess ? (
              <div className="flex flex-col items-center justify-center py-16 gap-3 text-success">
                <CheckCircle2 size={40} />
                <p className="text-base font-bold text-foreground">Listing Posted!</p>
                <p className="text-sm text-muted-foreground">Your listing is now live on the marketplace.</p>
              </div>
            ) : (
              <form onSubmit={handleCreateListing} className="px-6 py-5 flex flex-col gap-4">
                {/* Verification notice */}
                {!verifiedLoading && !isVerified && (
                  <div className="flex items-start gap-2.5 p-3 bg-warning/10 border border-warning/30 rounded-xl">
                    <AlertCircle size={15} className="text-warning shrink-0 mt-0.5" />
                    <div>
                      <p className="text-xs font-semibold text-foreground">Verification Required</p>
                      <p className="text-xs text-muted-foreground mt-0.5">You must be a verified user to post listings. Complete verification in your profile settings.</p>
                    </div>
                  </div>
                )}

                {submitError && (
                  <div className="flex items-start gap-2.5 p-3 bg-danger/10 border border-danger/30 rounded-xl">
                    <AlertCircle size={15} className="text-danger shrink-0 mt-0.5" />
                    <p className="text-xs text-danger">{submitError}</p>
                  </div>
                )}

                <div>
                  <label className="block text-xs font-semibold text-foreground mb-1.5">Title <span className="text-danger">*</span></label>
                  <input
                    required
                    type="text"
                    placeholder="e.g. Premium Logo Design Service"
                    value={form.title}
                    onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                    className="w-full px-3 py-2 text-sm bg-muted border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-foreground mb-1.5">Category <span className="text-danger">*</span></label>
                  <div className="grid grid-cols-3 gap-2">
                    {(Object.entries(CATEGORY_CONFIG) as [ListingCategory, typeof CATEGORY_CONFIG[ListingCategory]][]).map(([key, cfg]) => (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setForm((f) => ({ ...f, category: key }))}
                        className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border text-xs font-semibold transition-all ${form.category === key ? `${cfg.bg} ${cfg.color} border-current` : 'border-border text-muted-foreground hover:bg-muted'}`}
                      >
                        {cfg.icon}
                        <span className="text-center leading-tight">{cfg.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-foreground mb-1.5">Description <span className="text-danger">*</span></label>
                  <textarea
                    required
                    rows={3}
                    placeholder="Describe what you're selling, condition, what's included..."
                    value={form.description}
                    onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                    className="w-full px-3 py-2 text-sm bg-muted border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary resize-none"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-foreground mb-1.5">Price <span className="text-danger">*</span></label>
                    <input
                      required
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                      value={form.price}
                      onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))}
                      className="w-full px-3 py-2 text-sm bg-muted border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-foreground mb-1.5">Currency</label>
                    <select
                      value={form.currency}
                      onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value }))}
                      className="w-full px-3 py-2 text-sm bg-muted border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30"
                    >
                      {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-foreground mb-1.5">Location</label>
                  <input
                    type="text"
                    placeholder="e.g. Nairobi, KE or Remote"
                    value={form.location}
                    onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
                    className="w-full px-3 py-2 text-sm bg-muted border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-foreground mb-1.5">How to Contact You <span className="text-danger">*</span></label>
                  <input
                    required
                    type="text"
                    placeholder="e.g. WhatsApp +27 82 000 0000 or email@example.com"
                    value={form.contact_info}
                    onChange={(e) => setForm((f) => ({ ...f, contact_info: e.target.value }))}
                    className="w-full px-3 py-2 text-sm bg-muted border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                  />
                  <p className="text-[10px] text-muted-foreground mt-1">Buyers will see this to arrange payment off-platform.</p>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-foreground mb-1.5">Tags (comma-separated)</label>
                  <input
                    type="text"
                    placeholder="e.g. design, logo, branding"
                    value={form.tags}
                    onChange={(e) => setForm((f) => ({ ...f, tags: e.target.value }))}
                    className="w-full px-3 py-2 text-sm bg-muted border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                  />
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => { setShowCreateModal(false); setSubmitError(''); }}
                    className="flex-1 py-2.5 text-sm font-semibold text-muted-foreground border border-border rounded-xl hover:bg-muted transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-semibold text-primary-foreground bg-primary hover:bg-cyan-600 rounded-xl transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {submitting ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />}
                    {submitting ? 'Posting...' : 'Post Listing'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
